import { StandardEvents } from '@shopify/events';

class OzProductGallery extends HTMLElement {
  controller;

  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('keydown', this.handleKeyDown, { signal });

    const eventTarget = this.closest('.shopify-section, dialog');
    eventTarget?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-ozpg-slide]'));
  }

  get currentIndex() {
    const visibleSlide = this.querySelector('[data-ozpg-slide]:not([hidden])');
    const index = Number.parseInt(visibleSlide?.getAttribute('data-ozpg-slide') || '0', 10);
    return Number.isFinite(index) ? index : 0;
  }

  selectMedia(requestedIndex) {
    const slides = this.slides;
    if (!slides.length) return;

    const index = ((requestedIndex % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', String(!active));

      if (!active) {
        slide.querySelectorAll('video').forEach((video) => video.pause());
      }
    });

    this.querySelectorAll('[data-ozpg-index]').forEach((thumbnail) => {
      const thumbnailIndex = Number.parseInt(thumbnail.getAttribute('data-ozpg-index') || '-1', 10);
      const active = thumbnailIndex === index;
      thumbnail.setAttribute('aria-selected', String(active));

      if (active) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        thumbnail.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      }
    });

    const current = this.querySelector('[data-ozpg-current]');
    if (current) current.textContent = String(index + 1);
  }

  openZoom(trigger) {
    const dialog = this.querySelector('[data-ozpg-dialog]');
    const image = dialog?.querySelector('[data-ozpg-dialog-image]');
    const source = trigger.getAttribute('data-full-image');

    if (!(dialog instanceof HTMLDialogElement) || !(image instanceof HTMLImageElement) || !source) return;

    image.src = source;
    image.alt = trigger.getAttribute('data-image-alt') || '';

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  closeZoom(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    const image = dialog.querySelector('[data-ozpg-dialog-image]');
    if (image instanceof HTMLImageElement) {
      image.removeAttribute('src');
      image.alt = '';
    }
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('[data-ozpg-index]');
    if (thumbnail && this.contains(thumbnail)) {
      event.preventDefault();
      const index = Number.parseInt(thumbnail.getAttribute('data-ozpg-index') || '0', 10);
      this.selectMedia(index);
      return;
    }

    const stepButton = event.target.closest('[data-ozpg-step]');
    if (stepButton && this.contains(stepButton)) {
      event.preventDefault();
      const step = Number.parseInt(stepButton.getAttribute('data-ozpg-step') || '0', 10);
      this.selectMedia(this.currentIndex + step);
      return;
    }

    const zoomTrigger = event.target.closest('[data-ozpg-zoom]');
    if (zoomTrigger && this.contains(zoomTrigger)) {
      event.preventDefault();
      this.openZoom(zoomTrigger);
      return;
    }

    const closeButton = event.target.closest('[data-ozpg-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.closeZoom(closeButton.closest('[data-ozpg-dialog]'));
      return;
    }

    const dialog = event.target.closest('[data-ozpg-dialog]');
    if (dialog && event.target === dialog && this.contains(dialog)) {
      this.closeZoom(dialog);
    }
  };

  handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const dialog = event.target.closest('[data-ozpg-dialog]');
    if (dialog && event.key === 'Escape') {
      this.closeZoom(dialog);
      return;
    }

    if (event.target.closest('button, a, input, select, textarea, video, model-viewer')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.selectMedia(this.currentIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.selectMedia(this.currentIndex + 1);
    }
  };

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const replacement = detail?.html?.querySelector('oz-product-gallery[data-ozpg-gallery]');
        if (!replacement || !this.isConnected) return;
        this.replaceWith(replacement);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[oz-product-gallery] Variant gallery update failed:', error);
        }
      });
  };
}

if (!customElements.get('oz-product-gallery')) {
  customElements.define('oz-product-gallery', OzProductGallery);
}
